/**
 * Diferidos.tsx — lo cobrado por adelantado, y el devengo del mes.
 *
 * ── ▶ LO PRIMERO QUE SE VE ES EL CUADRE, NO LA LISTA ──────────────────────
 *
 * Casi todo lo que puede salir mal en este módulo **deja el balance cuadrando**:
 * devengar dos veces reconoce el ingreso dos veces, no devengar lo reconoce de
 * menos, y una cuenta de contrato acreditada sin obligación detrás es dinero que
 * nadie va a devengar nunca. Ninguno de los tres mueve un total.
 *
 * Así que la pantalla abre con la única cifra que los ve: el pendiente del
 * auxiliar contra el saldo contable, por dos caminos distintos. Si coinciden, no
 * es porque una se haya copiado de la otra.
 *
 * ── Y después qué va a pasar, antes del botón ─────────────────────────────
 *
 * Devengar escribe un asiento. La cuota de cada obligación se enseña ANTES de
 * ofrecer el botón: quien lo aprieta ya sabe el número. Es la misma regla de
 * `/activos`, y acá el precio de averiguar apretando es reconocer ingreso.
 */
import { useCallback, useEffect, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { anioYMesDeHoy } from '../nucleo/hoy.ts';
import { esCero } from '../nucleo/dinero.ts';
import { puedeRegistrar, useMesDeTrabajo, useSesion } from '../nucleo/sesion.tsx';
import { nombreDe, useCuentasDeMovimiento } from '../nucleo/catalogo.tsx';
import { CampoDeDimension } from '../componentes/CampoDeDimension.tsx';
import {
  agregarObligacion, darDeBajaObligacion, devengarPeriodo, traerContrapartes,
  traerDiferidos, FalloDeApi,
  type Contraparte, type CuadreDeDiferidos, type CuotaDeDevengo,
  type ObligacionDiferida,
} from '../datos/libros.ts';

const MESES = Array.from({ length: 12 }, (_, i) => i + 1);

export function Diferidos() {
  const { t, monto, fecha, idioma } = useIdioma();
  const { pedir, sesion } = useSesion();
  const hojas = useCuentasDeMovimiento();

  /* ▶ Cada desplegable ofrece SÓLO las cuentas de su tipo, y no es cosmética:
     la base rechaza el resto con `SB236`, así que ofrecerlas es ofrecer un
     error. Es la lección que `/activos` pagó abriendo la pantalla y viendo
     «Capital social» entre las candidatas a gasto de depreciación. */
  const cuentasDeContrato = hojas.filter((c) => c.tipo === 'liability');
  const cuentasDeIngreso = hojas.filter((c) => c.tipo === 'income');
  const escribe = puedeRegistrar(sesion);

  // `hoy` es el calendario, para la fecha por omisión de un diferido nuevo (un
  // hecho real). El SELECTOR de mes parte del mes de trabajo (el abierto más
  // viejo sin cerrar), no del calendario.
  const hoy = anioYMesDeHoy();
  const trabajo = useMesDeTrabajo();
  const [anio, setAnio] = useState(trabajo.anio);
  const [mes, setMes] = useState(trabajo.mes);

  const [obligaciones, setObligaciones] = useState<readonly ObligacionDiferida[]>([]);
  const [previo, setPrevio] = useState<readonly CuotaDeDevengo[]>([]);
  const [cuadre, setCuadre] = useState<CuadreDeDiferidos | null>(null);
  const [clientes, setClientes] = useState<readonly Contraparte[]>([]);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [hecho, setHecho] = useState<string | null>(null);
  const [abrirForm, setAbrirForm] = useState(false);

  const primero = `${hoy.anio}-${String(hoy.mes).padStart(2, '0')}-01`;
  const [nuevo, setNuevo] = useState({
    descripcion: '', contraparte: '', cuentaDiferida: '', cuentaIngreso: '',
    fecha: primero, inicio: primero, importe: '', meses: '12', modalidad: '',
  });

  const cargar = useCallback(async () => {
    setFallo(null);
    try {
      const r = await traerDiferidos(pedir, anio, mes);
      setObligaciones(r.obligaciones);
      setPrevio(r.previo);
      setCuadre(r.cuadre);
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    }
  }, [pedir, anio, mes]);

  useEffect(() => { void cargar(); }, [cargar]);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const cs = await traerContrapartes(pedir, 'cliente');
        if (vivo) setClientes(cs);
      } catch {
        // La lista de clientes es una comodidad del formulario: si falla, el
        // resto de la pantalla —el cuadre y el devengo— sigue sirviendo.
      }
    })();
    return () => { vivo = false; };
  }, [pedir]);

  async function correr(fn: () => Promise<unknown>) {
    setOcupado(true);
    setFallo(null);
    try {
      await fn();
      await cargar();
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setOcupado(false);
    }
  }

  async function devengar() {
    setHecho(null);
    await correr(async () => {
      const r = await devengarPeriodo(pedir, anio, mes);
      setHecho(r.asiento === null
        ? t('dif.doneNone')
        : r.obligaciones === 1
          ? t('dif.doneOkOne', { asiento: String(r.asiento) })
          : t('dif.doneOk', {
            obligaciones: String(r.obligaciones), asiento: String(r.asiento),
          }));
    });
  }

  const total = previo.reduce((n, p) => n + Math.round(Number(p.cuota) * 100), 0) / 100;
  const moneda = previo[0]?.moneda ?? obligaciones[0]?.moneda_local ?? 'CRC';

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t('dif.title')}</h1>
          <p className="page-head__sub">{t('dif.sub')}</p>
        </div>
      </div>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}
      {hecho && (
        <div className="alert alert--ok" style={{ marginBottom: 'var(--s6)' }}>
          <span>{hecho}</span>
        </div>
      )}

      {/* ── El cuadre, primero ──────────────────────────────────────────── */}
      {/* ▶ Y NO se enseña cuando no hay nada que conciliar. Una empresa sin
          obligaciones y sin saldo en cuentas de contrato veía un «✔ el auxiliar
          cuadra · ₡ 0,00 contra ₡ 0,00» en lo primero de la pantalla: verde sobre
          nada y arriba de todo. Lo encontró la caminata, y la lección ya estaba
          escrita en el paso 1 del cierre — *una comprobación que no puede fallar
          entrena a no leerla*, y la que sí importa vive en la misma caja.

          Cuando NO cuadra se enseña siempre, tenga lo que tenga. */}
      {/* ▶ Y desde la `0114` también se enseña cuando hay PROYECTOS, aunque no haya
          ni una obligación: su pendiente entra en esta misma conciliación. */}
      {cuadre && (cuadre.obligaciones > 0 || cuadre.proyectos > 0 || !cuadre.cuadra
        || !esCero({ valor: cuadre.saldo_contable, moneda })) && (
        <section
          className={`alert ${cuadre.cuadra ? 'alert--ok' : 'alert--warn'}`}
          style={{ marginBottom: 'var(--s6)', display: 'block' }}
        >
          <strong>{t(cuadre.cuadra ? 'dif.tiesTitle' : 'dif.mismatchTitle')}</strong>
          <div className="small" style={{ marginTop: 'var(--s3)' }}>
            {t('dif.tiesDetail', {
              auxiliar: monto({ valor: cuadre.pendiente_auxiliar, moneda }),
              contable: monto({ valor: cuadre.saldo_contable, moneda }),
            })}
          </div>
          {/* ▶▶ LOS DOS AUXILIARES, SEPARADOS (`0114`). El total sumado diría que
              algo se movió sin decir cuál de los dos detalles abrir — y los dos se
              arreglan en pantallas distintas: uno acá y el otro en Proyectos. */}
          {cuadre.proyectos > 0 && (
            <div className="small" style={{ marginTop: 'var(--s3)' }}>
              {t('dif.twoLedgers', {
                obligaciones: monto({ valor: cuadre.pendiente_auxiliar, moneda }),
                cuantas: String(cuadre.obligaciones),
                proyectos: monto({ valor: cuadre.pendiente_proyectos, moneda }),
                cuantos: String(cuadre.proyectos),
              })}
            </div>
          )}
          {/* ▶ Y los que esta conciliación NO puede contar, dichos en voz alta:
              un proyecto con cuenta de contrato de ACTIVO no tiene su saldo en el
              diferido. Callarlo es cómo un cuadre termina verde por casualidad. */}
          {cuadre.proyectos_fuera > 0 && (
            <div className="small" style={{ marginTop: 'var(--s3)' }}>
              {t('dif.projectsOut', { cuantos: String(cuadre.proyectos_fuera) })}
            </div>
          )}
          {/* ▶▶ DOS FRASES, Y LAS SEPARÓ LA CAMINATA. Decía una sola —«sobran
              {diferencia}»— y la primera obligación dada de alta a mano la dejó
              diciendo **«sobran −₡ 971 666,67»**: un sobrante negativo, que no
              es nada. Y las dos direcciones no se arreglan igual:

                sobra en los libros   → hay dinero en cuentas de contrato que
                                        ninguna obligación explica
                falta en los libros   → hay obligaciones cuyo cobro no se asentó,
                                        o se devengó de más

              Es la lección de `SB192` en la factura de compra, en una
              conciliación en vez de en un código: *una frase que cubre dos casos
              manda a arreglar el que no es.* */}
          {!cuadre.cuadra && (
            <div className="small" style={{ marginTop: 'var(--s3)' }}>
              {/* ▶ El signo se MIRA, no se calcula. Convertir el importe a
                  número para compararlo con cero es aritmética de dinero en el
                  cliente, y el guardían 3 la cazó acá mismo. Un texto que
                  empieza por `-` es negativo sin convertir nada — §6.1. */}
              {t(cuadre.diferencia.startsWith('-') ? 'dif.mismatchUnder' : 'dif.mismatchOver', {
                diferencia: monto({
                  valor: cuadre.diferencia.replace('-', ''), moneda,
                }),
              })}
            </div>
          )}
          {/* ▶ D6 · lo que vence a más de doce meses se ENSEÑA y no se asienta:
              la reclasificación a no corriente pide un segundo asiento mensual
              de ida y vuelta, y eso se construye contra un contrato largo de
              verdad. Mientras tanto, el número está a la vista. */}
          {!esCero({ valor: cuadre.no_corriente, moneda }) && (
            <div className="small" style={{ marginTop: 'var(--s3)' }}>
              {t('dif.nonCurrent', {
                importe: monto({ valor: cuadre.no_corriente, moneda }),
              })}
            </div>
          )}
        </section>
      )}

      {/* ── El mes, y lo que se devengaría ──────────────────────────────── */}
      <section className="card" style={{ marginBottom: 'var(--s6)' }}>
        <div className="card__head"><h3>{t('dif.monthTitle')}</h3></div>

        <div className="row" style={{ padding: '0 var(--s7)', gap: 'var(--s4)', flexWrap: 'wrap' }}>
          <label>{t('dif.period')}
            <select
              className="select"
              value={`${anio}-${mes}`}
              onChange={(e) => {
                const [a, m] = e.target.value.split('-');
                setAnio(Number(a));
                setMes(Number(m));
                setHecho(null);
              }}
            >
              {MESES.map((m) => (
                <option key={m} value={`${anio}-${m}`}>
                  {`${anio}-${String(m).padStart(2, '0')}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        {previo.length === 0 ? (
          <p className="small muted" style={{ padding: 'var(--s6) var(--s7)' }}>
            {obligaciones.length === 0 ? t('dif.noneAtAll') : t('dif.noneThisMonth')}
          </p>
        ) : (
          <>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t('dif.obligation')}</th>
                    <th>{t('dif.term')}</th>
                    <th style={{ textAlign: 'right' }}>{t('dif.quota')}</th>
                  </tr>
                </thead>
                <tbody>
                  {previo.map((p) => (
                    <tr key={p.obligacion}>
                      <td>{p.descripcion}</td>
                      <td className="small muted">
                        {t('dif.termOf', {
                          mes: String(p.mes_de_plazo), total: String(p.meses),
                        })}
                        {/* ▶ El último mes lleva lo que falta y no la cuota, para
                            que la obligación cierre exacta en cero. Que se vea
                            distinto evita la pregunta «¿por qué cambió?». */}
                        {p.ultimo && <> · <strong>{t('dif.lastMonth')}</strong></>}
                      </td>
                      <td className="amount">{monto({ valor: p.cuota, moneda: p.moneda })}</td>
                    </tr>
                  ))}
                  <tr className="is-total">
                    <td colSpan={2}>{t('dif.total')}</td>
                    <td className="amount">
                      {monto({ valor: total.toFixed(2), moneda: previo[0]!.moneda })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {escribe && (
              <div className="row" style={{ padding: 'var(--s7)', gap: 'var(--s5)', flexWrap: 'wrap' }}>
                <button className="btn" type="button" disabled={ocupado} onClick={() => void devengar()}>
                  {t('dif.doIt')}
                </button>
                <span className="small muted">{t('dif.twiceIsFine')}</span>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── Las obligaciones ────────────────────────────────────────────── */}
      <section className="card">
        <div className="card__head">
          <h3>{t('dif.listTitle')}</h3>
          {escribe && (
            <button className="btn btn--ghost btn--sm" type="button"
              onClick={() => setAbrirForm((v) => !v)}>
              {abrirForm ? t('common.cancel') : t('dif.add')}
            </button>
          )}
        </div>

        {abrirForm && escribe && (
          <div className="stack" style={{ padding: '0 var(--s7) var(--s7)' }}>
            {/* ▶ El camino normal es la factura, y conviene decirlo acá: este
                formulario es para lo que viene de antes de Books —una apertura—
                o para un cobro que no pasó por una factura de venta. */}
            <p className="small muted">{t('dif.addHelp')}</p>
            <div className="grid grid--form">
              <label>{t('dif.description')}
                <input className="input" value={nuevo.descripcion}
                  onChange={(e) => setNuevo({ ...nuevo, descripcion: e.target.value })} />
              </label>
              <label>{t('dif.customer')}
                <select className="select" value={nuevo.contraparte}
                  onChange={(e) => setNuevo({ ...nuevo, contraparte: e.target.value })}>
                  <option value="">—</option>
                  {clientes.map((c) => (
                    <option key={c.codigo} value={c.codigo ?? ''}>{c.nombre}</option>
                  ))}
                </select>
              </label>
              <label>{t('dif.accountDeferred')} <span className="small muted">· {t('dif.onlyLiabilities')}</span>
                <select className="select" value={nuevo.cuentaDiferida}
                  onChange={(e) => setNuevo({ ...nuevo, cuentaDiferida: e.target.value })}>
                  <option value="">—</option>
                  {cuentasDeContrato.map((c) => (
                    <option key={c.codigo} value={c.codigo}>{c.codigo} · {nombreDe(c, idioma)}</option>
                  ))}
                </select>
              </label>
              <label>{t('dif.accountIncome')} <span className="small muted">· {t('dif.onlyIncome')}</span>
                <select className="select" value={nuevo.cuentaIngreso}
                  onChange={(e) => setNuevo({ ...nuevo, cuentaIngreso: e.target.value })}>
                  <option value="">—</option>
                  {cuentasDeIngreso.map((c) => (
                    <option key={c.codigo} value={c.codigo}>{c.codigo} · {nombreDe(c, idioma)}</option>
                  ))}
                </select>
              </label>
              <label>{t('dif.docDate')}
                <input className="input" type="date" value={nuevo.fecha}
                  onChange={(e) => setNuevo({ ...nuevo, fecha: e.target.value })} />
              </label>
              {/* ▶ Va aparte de la fecha del documento: el mantenimiento se
                  factura de 60 a 90 días antes de la renovación, así que la
                  factura es de noviembre y el servicio arranca en enero. */}
              <label>{t('dif.start')}
                <input className="input" type="date" value={nuevo.inicio}
                  onChange={(e) => setNuevo({ ...nuevo, inicio: e.target.value })} />
              </label>
              <label>{t('dif.amount')}
                <input className="input input--mono" inputMode="decimal" value={nuevo.importe}
                  onChange={(e) => setNuevo({ ...nuevo, importe: e.target.value })} />
              </label>
              <label>{t('dif.months')}
                <input className="input input--mono" inputMode="numeric" value={nuevo.meses}
                  onChange={(e) => setNuevo({ ...nuevo, meses: e.target.value })} />
              </label>
              <CampoDeDimension
                dimension="modalidad"
                valor={nuevo.modalidad}
                alCambiar={(v) => setNuevo({ ...nuevo, modalidad: v })}
              />
            </div>
            <div className="row" style={{ gap: 'var(--s4)' }}>
              <button
                className="btn" type="button"
                disabled={ocupado || !nuevo.descripcion || !nuevo.contraparte
                  || !nuevo.cuentaDiferida || !nuevo.cuentaIngreso || !nuevo.importe}
                onClick={() => void correr(async () => {
                  await agregarObligacion(pedir, {
                    descripcion: nuevo.descripcion,
                    contraparte: nuevo.contraparte,
                    cuentaDiferida: nuevo.cuentaDiferida,
                    cuentaIngreso: nuevo.cuentaIngreso,
                    fecha: nuevo.fecha,
                    inicio: nuevo.inicio,
                    importe: nuevo.importe,
                    meses: Number(nuevo.meses),
                    clasificacion: nuevo.modalidad ? { modalidad: nuevo.modalidad } : {},
                  });
                  setAbrirForm(false);
                  setNuevo({ ...nuevo, descripcion: '', importe: '' });
                })}
              >
                {t('dif.save')}
              </button>
            </div>
          </div>
        )}

        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t('dif.obligation')}</th>
                <th>{t('dif.customer')}</th>
                <th>{t('dif.start')}</th>
                <th style={{ textAlign: 'right' }}>{t('dif.amount')}</th>
                <th style={{ textAlign: 'right' }}>{t('dif.pending')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {obligaciones.map((o) => (
                <tr key={o.id}>
                  <td>
                    {o.descripcion}
                    {o.dado_de_baja && (
                      <> <span className="chip chip--soft">{t('dif.ended')}</span></>
                    )}
                  </td>
                  <td className="small">{o.contraparte_nombre}</td>
                  <td className="small">
                    {fecha(o.inicio)}
                    <span className="muted"> · {t('dif.monthsOf', { meses: String(o.meses) })}</span>
                  </td>
                  <td className="amount">
                    {monto({ valor: o.importe_funcional, moneda: o.moneda_local })}
                    {/* ▶ La moneda original sólo se enseña cuando NO es la de los
                        libros: repetirla en todas las filas haría que la que sí
                        difiere deje de saltar. */}
                    {o.moneda !== o.moneda_local && (
                      <> <span className="cur cur--usd">{o.moneda}</span></>
                    )}
                  </td>
                  <td className="amount">
                    {monto({ valor: o.pendiente, moneda: o.moneda_local })}
                  </td>
                  <td>
                    {escribe && !o.dado_de_baja && (
                      <button
                        className="btn btn--ghost btn--sm" type="button" disabled={ocupado}
                        onClick={() => void correr(() => darDeBajaObligacion(
                          pedir, o.id, `${anio}-${String(mes).padStart(2, '0')}-01`))}
                      >
                        {t('dif.end')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {obligaciones.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 'var(--s7)' }}>
                    {t('dif.noneAtAll')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="small muted" style={{ padding: '0 var(--s7) var(--s7)' }}>
          {t('dif.endNote')}
        </p>
      </section>
    </div>
  );
}
